import type { RenderComponentProps } from "replo-runtime/shared/renderComponent";
import type { ShopifySellingPlan } from "schemas/runtimeProduct";

import * as React from "react";

import { useKlaviyoIdentifiersStore } from "replo-runtime/store/contexts/KlaviyoIdentifiers/context";
import { useRuntimeActiveStateStore } from "replo-runtime/store/contexts/RuntimeActiveState/hooks";

import {
  RenderEnvironmentContext,
  RuntimeHooksContext,
  ShopifyStoreContext,
  useRuntimeContext,
} from "../../../shared/runtime-context";
import { mapNull } from "../../../shared/utils/optional";
import { withLiquidAlternate } from "../../../shared/utils/withLiquidAlternate";
import { executeAction } from "../../AlchemyAction";
import ReploLiquidChunk from "../ReploLiquid/ReploLiquidChunk";

const DEFAULT_ONE_TIME_PURCHASE_TEXT = "One-time purchase";

const SellingPlanSelectDropdown: React.FC<RenderComponentProps> = ({
  component,
  componentAttributes,
  context,
}) => {
  const { templateProduct } = useRuntimeContext(ShopifyStoreContext);
  const products = useRuntimeContext(RuntimeHooksContext).useShopifyProducts();
  const { isEditorApp } = useRuntimeContext(RenderEnvironmentContext);
  const componentId = component.id;
  const includeOneTimePurchase =
    component.props._includeOneTimePurchase ?? true;
  const oneTimePurchaseText =
    component.props._oneTimePurchaseText ?? DEFAULT_ONE_TIME_PURCHASE_TEXT;
  const sellingPlans: ShopifySellingPlan[] =
    context.attributes?._sellingPlans ?? [];
  const filteredSellingPlans = sellingPlans.filter((sellingPlan) => {
    return (
      !component.props?._productSellingPlans ||
      component.props?._productSellingPlans.includes(Number(sellingPlan.id))
    );
  });

  const runtimeActiveStateStore = useRuntimeActiveStateStore();
  const klaviyoIdentifiersStore = useKlaviyoIdentifiersStore();

  if (!isEditorApp && sellingPlans.length === 0) {
    return null;
  }

  return (
    <select
      {...componentAttributes}
      role="listbox"
      value={
        mapNull(context.attributes?._selectedSellingPlan?.id, String) ?? "null"
      }
      // Note (Noah, 2024-09-11, USE-1209): Stop propagation on click so that any
      // interactions on parent containers don't get triggered
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        void executeAction(
          {
            id: `replo:selectSellingPlan`,
            type: "setActiveSellingPlan",
            value: {
              sellingPlanId:
                e.target.value === "null"
                  ? { __reploOneTimePurchase: true }
                  : Number(e.target.value),
            },
          },
          {
            componentId: componentId,
            componentContext: context,
            products: products,
            templateProduct,
            stores: {
              runtimeActiveState: runtimeActiveStateStore,
              klaviyoIdentifiers: klaviyoIdentifiersStore,
            },
          },
        );
      }}
    >
      {includeOneTimePurchase &&
        oneTimePurchaseText &&
        filteredSellingPlans.length > 0 && (
          <option value="null" role="option">
            {oneTimePurchaseText}
          </option>
        )}
      {filteredSellingPlans.map((sellingPlan: ShopifySellingPlan) => (
        <option value={sellingPlan.id} key={sellingPlan.id} role="option">
          {sellingPlan.name}
        </option>
      ))}
    </select>
  );
};

const SellingPlanSelectDropdownLiquid: React.FC<RenderComponentProps> = ({
  componentAttributes,
  component,
  context,
}) => {
  const includeOneTimePurchase =
    component.props._includeOneTimePurchase ?? true;
  const oneTimePurchaseText =
    component.props._oneTimePurchaseText ?? DEFAULT_ONE_TIME_PURCHASE_TEXT;

  const sellingPlans: ShopifySellingPlan[] =
    context.attributes?._sellingPlans ?? [];
  const selectedSellingPlans = component.props._productSellingPlans;
  const selectedSellingPlansString = selectedSellingPlans?.join(",") ?? "";
  const filteredSellingPlans = sellingPlans.filter((sellingPlan) => {
    return (
      !selectedSellingPlans ||
      selectedSellingPlans.includes(Number(sellingPlan.id))
    );
  });

  const { isEditorApp } = useRuntimeContext(RenderEnvironmentContext);

  if (!isEditorApp && filteredSellingPlans.length === 0) {
    return null;
  }

  return (
    <ReploLiquidChunk>
      {`{% if reploSortedSellingPlans != blank %}`}
      {`{% capture selectedSellingPlanIds %}${selectedSellingPlansString}{% endcapture %}`}
      <select {...componentAttributes} role="listbox">
        {includeOneTimePurchase && (
          <option value="null" role="option">
            {oneTimePurchaseText}
          </option>
        )}

        {`{% for reploRepeatedSellingPlan in reploSortedSellingPlans %}`}
        {`{% if selectedSellingPlanIds == blank or selectedSellingPlanIds contains reploRepeatedSellingPlan.id %}`}
        {`{% if forloop.first %}`}
        <option
          value="{{reploRepeatedSellingPlan.id}}"
          key="{{reploRepeatedSellingPlan.id}}"
          role="option"
          selected
        >
          {"{{reploRepeatedSellingPlan.name}}"}
        </option>
        {`{% else %}`}
        <option
          value="{{reploRepeatedSellingPlan.id}}"
          key="{{reploRepeatedSellingPlan.id}}"
          role="option"
        >
          {"{{reploRepeatedSellingPlan.name}}"}
        </option>
        {`{% endif %}`}
        {`{% endif %}`}
        {"{% endfor %}"}
      </select>
      {`{% endif %}`}
    </ReploLiquidChunk>
  );
};

export default withLiquidAlternate(
  SellingPlanSelectDropdown,
  SellingPlanSelectDropdownLiquid,
);
